import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Bell, Globe, Lock, LogOut, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";

const Parametres = () => {
  useSEO({ noindex: true });
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, locale } = useLanguage("artisan");

  // Notifications state (local preferences)
  const [notifMessages, setNotifMessages] = useState(true);
  const [notifRdv, setNotifRdv] = useState(true);
  const [notifAnnonces, setNotifAnnonces] = useState(true);

  // Password change
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast({ title: t("parametres.toast_error"), description: t("parametres.toast_too_short"), variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t("parametres.toast_error"), description: t("parametres.toast_mismatch"), variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPassword(false);
    if (error) {
      toast({ title: t("parametres.toast_error"), description: error.message, variant: "destructive" });
    } else {
      toast({ title: t("parametres.toast_success"), description: t("parametres.toast_success_desc") });
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const switchLocale = (newLocale: string) => {
    const current = window.location.pathname;
    // Remplace /fr/ ou /ar/ au début par le nouveau locale
    const updated = current.replace(/^\/(fr|ar)(\/|$)/, `/${newLocale}$2`);
    navigate(updated !== current ? updated : `/${newLocale}/parametres`);
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <Navbar />
      <main className="container max-w-lg mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-display font-bold">{t("parametres.title")}</h1>

        {/* Notifications */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5 text-primary" />
              {t("parametres.notif_title")}
            </CardTitle>
            <CardDescription>{t("parametres.notif_desc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="notif-messages">{t("parametres.notif_messages")}</Label>
              <Switch id="notif-messages" checked={notifMessages} onCheckedChange={setNotifMessages} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <Label htmlFor="notif-rdv">{t("parametres.notif_rdv")}</Label>
              <Switch id="notif-rdv" checked={notifRdv} onCheckedChange={setNotifRdv} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <Label htmlFor="notif-annonces">{t("parametres.notif_annonces")}</Label>
              <Switch id="notif-annonces" checked={notifAnnonces} onCheckedChange={setNotifAnnonces} />
            </div>
          </CardContent>
        </Card>

        {/* Langue */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="h-5 w-5 text-primary" />
              {t("parametres.lang_title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button
                variant={locale === "fr" ? "default" : "outline"}
                size="sm"
                onClick={() => switchLocale("fr")}
              >
                Français
              </Button>
              <Button
                variant={locale === "ar" ? "default" : "outline"}
                size="sm"
                onClick={() => switchLocale("ar")}
              >
                العربية
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mot de passe */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="h-5 w-5 text-primary" />
              {t("parametres.password_title")}
            </CardTitle>
            <CardDescription>{t("parametres.password_desc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t("parametres.label_new_password")}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t("parametres.placeholder_new_password")}
                  maxLength={128}
                />
                <button
                  type="button"
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowNew(!showNew)}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t("parametres.label_confirm")}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("parametres.placeholder_confirm")}
                maxLength={128}
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !newPassword}
              className="w-full"
            >
              {changingPassword ? t("parametres.updating") : t("parametres.update_password")}
            </Button>
          </CardContent>
        </Card>

        {/* Déconnexion */}
        <Card>
          <CardContent className="pt-6">
            <Button
              variant="destructive"
              className="w-full gap-2"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4" />
              {t("parametres.sign_out")}
            </Button>
          </CardContent>
        </Card>
      </main>
      <ArtisanBottomNav />
    </div>
  );
};

export default Parametres;
